import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';

import PageCard from '../../PageCard/PageCard';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Segmented } from '../../ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import { GroupKey, ReportRow } from '../../../model/organization.model';
import {
  formatHours,
  formatMoney,
  formatPercent,
  memberById,
  projectById,
  projects,
  reportRows,
} from './orgMock';

/** Mock periods — the multiplier stands in for "a different month of data". */
const PERIODS = [
  { value: 'jan', label: 'January 2026', factor: 1 },
  { value: 'dec', label: 'December 2025', factor: 0.92 },
  { value: 'nov', label: 'November 2025', factor: 1.08 },
];

const GROUP_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'project', label: 'Project' },
  { value: 'member', label: 'Person' },
  { value: 'workType', label: 'Work type' },
];

interface Node {
  key: string;
  label: string;
  color?: string;
  /** Whether this row stands for a single project — only then is a budget meaningful. */
  isProject: boolean;
  hours: number;
  billableHours: number;
  revenue: number;
  projectIds: string[];
  children: Node[];
}

const labelFor = (row: ReportRow, key: GroupKey, level: 'top' | 'sub') => {
  const project = projectById(row.projectId);
  const member = memberById(row.memberId);
  const projectLabel = `${project?.client} · ${project?.name}`;

  if (key === 'client') return level === 'top' ? project?.client ?? '' : project?.name ?? '';
  if (key === 'project') return level === 'top' ? projectLabel : member?.name ?? '';
  if (key === 'member') return level === 'top' ? member?.name ?? '' : projectLabel;
  return level === 'top' ? row.workType : projectLabel;
};

const emptyNode = (key: string, label: string, isProject: boolean, color?: string): Node => ({
  key,
  label,
  color,
  isProject,
  hours: 0,
  billableHours: 0,
  revenue: 0,
  projectIds: [],
  children: [],
});

const accumulate = (node: Node, row: ReportRow, revenue: number) => {
  node.hours += row.hours;
  node.billableHours += row.billableHours;
  node.revenue += revenue;
  if (!node.projectIds.includes(row.projectId)) node.projectIds.push(row.projectId);
};

const ReportsPage: React.FC = () => {
  const [period, setPeriod] = useState('jan');
  const [groupKey, setGroupKey] = useState<GroupKey>('client');
  const [client, setClient] = useState('all');
  const [billableOnly, setBillableOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<string[]>([]);

  const factor = PERIODS.find(item => item.value === period)?.factor ?? 1;

  const clients = useMemo(
    () => Array.from(new Set(projects.map(project => project.client))),
    [],
  );

  const rows = useMemo(
    () =>
      reportRows
        .filter(row => {
          const project = projectById(row.projectId);
          if (!project) return false;
          if (client !== 'all' && project.client !== client) return false;
          if (billableOnly && !project.billable) return false;
          return true;
        })
        .map(row => ({
          ...row,
          hours: Math.round(row.hours * factor),
          billableHours: Math.round(row.billableHours * factor),
        })),
    [client, billableOnly, factor],
  );

  const tree = useMemo(() => {
    const byTop = new Map<string, Node>();
    // A budget bar is only honest on a row holding a project's *whole* burn. Grouping by
    // member or work type splits each project across rows, so no row qualifies there.
    const topIsProject = groupKey === 'project';
    const subIsProject = groupKey === 'client';

    rows.forEach(row => {
      const project = projectById(row.projectId);
      const revenue = row.billableHours * (project?.rate ?? 0);

      const topLabel = labelFor(row, groupKey, 'top');
      let top = byTop.get(topLabel);
      if (!top) {
        const showColor = groupKey === 'client' || groupKey === 'project';
        top = emptyNode(topLabel, topLabel, topIsProject, showColor ? project?.color : undefined);
        byTop.set(topLabel, top);
      }
      accumulate(top, row, revenue);

      const subLabel = labelFor(row, groupKey, 'sub');
      let sub = top.children.find(child => child.label === subLabel);
      if (!sub) {
        sub = emptyNode(`${topLabel}/${subLabel}`, subLabel, subIsProject, project?.color);
        top.children.push(sub);
      }
      accumulate(sub, row, revenue);
    });

    return Array.from(byTop.values()).sort((a, b) => b.hours - a.hours);
  }, [rows, groupKey]);

  const totals = tree.reduce(
    (acc, node) => ({
      hours: acc.hours + node.hours,
      billableHours: acc.billableHours + node.billableHours,
      revenue: acc.revenue + node.revenue,
    }),
    { hours: 0, billableHours: 0, revenue: 0 },
  );

  const averageRate = totals.billableHours > 0 ? totals.revenue / totals.billableHours : 0;

  const toggle = (key: string) =>
    setCollapsed(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]));

  const exportCsv = () => {
    const header = ['Client', 'Project', 'Person', 'Work type', 'Hours', 'Billable', 'Revenue'];
    const lines = rows.map(row => {
      const project = projectById(row.projectId);
      return [
        project?.client ?? '',
        project?.name ?? '',
        memberById(row.memberId)?.name ?? '',
        row.workType,
        row.hours,
        row.billableHours,
        row.billableHours * (project?.rate ?? 0),
      ].join(';');
    });
    const csv = [header.join(';'), ...lines].join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-${period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** A budget bar only makes sense on a row holding one project's complete burn. */
  const budgetOf = (node: Node) => {
    if (!node.isProject || node.projectIds.length !== 1) return null;
    const project = projectById(node.projectIds[0]);
    if (!project?.budgetHours) return null;
    return { used: node.hours, total: project.budgetHours };
  };

  const title = (
    <div>
      <div className="text-base font-medium leading-tight">Time report</div>
      <div className="text-xs text-muted-foreground">
        Rates and totals are computed server-side — employees never see them
      </div>
    </div>
  );

  return (
    <PageCard
      title={title}
      extra={
        <Button size="sm" onClick={exportCsv}>
          <Download />
          CSV
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-8 w-[168px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map(item => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={client} onValueChange={setClient}>
          <SelectTrigger className="h-8 w-[168px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map(item => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={billableOnly}
            onCheckedChange={value => setBillableOnly(value === true)}
          />
          Billable only
        </label>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Group by</span>
          <Segmented
            options={GROUP_OPTIONS}
            value={groupKey}
            onChange={value => setGroupKey(value as GroupKey)}
          />
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Hours" value={formatHours(totals.hours)} />
        <Stat
          label="Billable"
          value={formatHours(totals.billableHours)}
          hint={formatPercent(totals.billableHours, totals.hours)}
        />
        <Stat label="Revenue" value={formatMoney(totals.revenue)} />
        <Stat label="Average rate" value={`${formatMoney(averageRate)}/h`} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-separate border-spacing-0">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-muted-foreground">
              <th className="border-b border-border px-3 py-2 text-left font-normal">
                {GROUP_OPTIONS.find(option => option.value === groupKey)?.label}
              </th>
              <th className="w-24 border-b border-border px-3 py-2 text-right font-normal">Hours</th>
              <th className="w-28 border-b border-border px-3 py-2 text-right font-normal">Billable</th>
              <th className="w-36 border-b border-border px-3 py-2 text-right font-normal">Revenue</th>
              <th className="w-56 border-b border-border px-3 py-2 text-left font-normal">Budget</th>
            </tr>
          </thead>
          <tbody>
            {tree.map(node => {
              const isCollapsed = collapsed.includes(node.key);
              const budget = budgetOf(node);

              return (
                <React.Fragment key={node.key}>
                  <tr>
                    <td className="border-b border-border/60 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggle(node.key)}
                        className="flex items-center gap-2 text-left text-sm"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        )}
                        {node.color && (
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-sm"
                            style={{ backgroundColor: node.color }}
                          />
                        )}
                        <span className="font-medium">{node.label}</span>
                      </button>
                    </td>
                    <td className="border-b border-border/60 px-3 py-2 text-right text-sm tabular-nums">
                      {formatHours(node.hours)}
                    </td>
                    <td className="border-b border-border/60 px-3 py-2 text-right text-sm tabular-nums text-muted-foreground">
                      {formatHours(node.billableHours)}
                    </td>
                    <td className="border-b border-border/60 px-3 py-2 text-right text-sm tabular-nums">
                      {node.revenue > 0 ? formatMoney(node.revenue) : <span className="text-muted-foreground/40">·</span>}
                    </td>
                    <td className="border-b border-border/60 px-3 py-2">
                      {budget ? <BudgetBar used={budget.used} total={budget.total} /> : null}
                    </td>
                  </tr>

                  {!isCollapsed &&
                    node.children
                      .slice()
                      .sort((a, b) => b.hours - a.hours)
                      .map(child => {
                        const childBudget = budgetOf(child);
                        return (
                          <tr key={child.key}>
                            <td className="border-b border-border/30 py-1.5 pl-[38px] pr-3 text-sm text-foreground/90">
                              {child.label}
                            </td>
                            <td className="border-b border-border/30 px-3 py-1.5 text-right text-sm tabular-nums">
                              {formatHours(child.hours)}
                            </td>
                            <td className="border-b border-border/30 px-3 py-1.5 text-right text-sm tabular-nums text-muted-foreground">
                              {formatHours(child.billableHours)}
                            </td>
                            <td className="border-b border-border/30 px-3 py-1.5 text-right text-sm tabular-nums text-muted-foreground">
                              {child.revenue > 0 ? formatMoney(child.revenue) : '·'}
                            </td>
                            <td className="border-b border-border/30 px-3 py-1.5">
                              {childBudget ? <BudgetBar used={childBudget.used} total={childBudget.total} /> : null}
                            </td>
                          </tr>
                        );
                      })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="px-3 py-2 text-sm font-medium">Total</td>
              <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                {formatHours(totals.hours)}
              </td>
              <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                {formatHours(totals.billableHours)}
              </td>
              <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                {formatMoney(totals.revenue)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </PageCard>
  );
};

const BudgetBar: React.FC<{ used: number; total: number }> = ({ used, total }) => {
  const share = Math.min(used / total, 1);
  const over = used > total;

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full transition-all',
            over ? 'bg-destructive' : share > 0.85 ? 'bg-amber-500' : 'bg-foreground',
          )}
          style={{ width: `${share * 100}%` }}
        />
      </div>
      <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
        {Math.round((used / total) * 100)}% · {total} h
      </span>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-md border border-border px-3 py-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="flex items-baseline gap-1.5">
      <span className="text-xl font-semibold tabular-nums">{value}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  </div>
);

export default ReportsPage;
